import { Suspense, useState, type ReactNode } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api, type AuthUser } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { AppLayout, type NavSection } from '@/layouts/AppLayout';
import { Loading } from '@/components/ui';

import Login from '@/pages/auth/Login';
import Setup, { SetupLoading } from '@/pages/auth/Setup';

// Client portal
import ClientDashboard from '@/pages/client/Dashboard';
import Bots from '@/pages/client/Bots';
import BotDetail from '@/pages/client/BotDetail';
import BotGroups from '@/pages/client/BotGroups';
import VicidialServers from '@/pages/client/VicidialServers';
import VicidialUsers from '@/pages/client/VicidialUsers';
import Assignments from '@/pages/client/Assignments';
import Calls from '@/pages/client/Calls';
import CallDetail from '@/pages/client/CallDetail';
import LiveCalls from '@/pages/client/LiveCalls';
import Leads from '@/pages/client/Leads';
import QualifiedLeads from '@/pages/client/QualifiedLeads';
import Transfers from '@/pages/client/Transfers';
import Usage from '@/pages/client/Usage';
import Allowances from '@/pages/client/Allowances';
import Billing from '@/pages/client/Billing';
import Analytics from '@/pages/client/Analytics';
import Sip from '@/pages/client/Sip';
import TeamUsers from '@/pages/client/TeamUsers';
import Settings from '@/pages/client/Settings';
import Compliance from '@/pages/client/Compliance';

// Super admin
import AdminOverview from '@/pages/admin/Overview';
import AdminSetup from '@/pages/admin/Setup';
import AdminLiveCalls from '@/pages/admin/LiveCalls';
import AdminStats from '@/pages/admin/Stats';
import AdminLeads from '@/pages/admin/Leads';
import AdminQualification from '@/pages/admin/Qualification';
import AdminDispositions from '@/pages/admin/Dispositions';
import AdminProviderStatus from '@/pages/admin/ProviderStatus';
import Clients from '@/pages/admin/Clients';
import AccessRequests from '@/pages/admin/AccessRequests';
import ClientDetail from '@/pages/admin/ClientDetail';
import Providers from '@/pages/admin/Providers';
import ServicesCatalog from '@/pages/admin/ServicesCatalog';
import Templates from '@/pages/admin/Templates';
import Composer from '@/pages/admin/Composer';
import Revenue from '@/pages/admin/Revenue';
import Costs from '@/pages/admin/Costs';
import Plans from '@/pages/admin/Plans';
import PlatformUsers from '@/pages/admin/PlatformUsers';
import AuditLogs from '@/pages/admin/AuditLogs';
import SystemLogs from '@/pages/admin/SystemLogs';
import Alerting from '@/pages/admin/Alerting';
import Notifications from '@/pages/admin/Notifications';
import {
  ForgotPassword,
  RequestAccess,
  ResetPassword,
} from '@/pages/auth/PublicAuth';
import SystemInfo from '@/pages/admin/SystemInfo';
import AdminCallDetail from '@/pages/admin/CallDetail';
import Diagnostics from '@/pages/admin/Diagnostics';
import AsteriskChannels from '@/pages/admin/AsteriskChannels';
import VoiceCache from '@/pages/admin/VoiceCache';
import Soundboards from '@/pages/admin/Soundboards';
import SoundboardBots from '@/pages/client/SoundboardBots';
import PlatformSettings from '@/pages/admin/PlatformSettings';
import AdminBranding from '@/pages/admin/Branding';
import AdminCalls from '@/pages/admin/Calls';
import Licensing from '@/pages/admin/Licensing';
import Invoices from '@/pages/admin/Invoices';
import Subscription from '@/pages/client/Subscription';

const CLIENT_NAV: NavSection[] = [
  {
    items: [
      { to: '/', label: 'Dashboard', end: true, permission: 'dashboard:read', icon: 'gauge' },
      { to: '/calls/live', label: 'Live calls', permission: 'calls:read', icon: 'waves' },
    ],
  },
  {
    title: 'Bots',
    items: [
      { to: '/bots', label: 'Glee AI Bots', permission: 'bots:read', icon: 'bot' },
      // Its own entry, not a filter on the AI bots: a different product,
      // sold separately, with nothing on it for a client to configure.
      { to: '/soundboard-bots', label: 'Soundboard Bots', permission: 'bots:read', icon: 'music' },
      { to: '/bot-groups', label: 'Bot groups', permission: 'bot_groups:read', icon: 'layers' },
    ],
  },
  {
    title: 'Dialer',
    items: [
      { to: '/vicidial-servers', label: 'Vicidial servers', permission: 'settings:read', icon: 'server' },
      { to: '/vicidial-users', label: 'Vicidial users', permission: 'vicidial_users:read', icon: 'headset' },
      { to: '/assignments', label: 'Assignments', permission: 'bot_assignments:read', icon: 'link' },
    ],
  },
  {
    title: 'Activity',
    items: [
      { to: '/calls', label: 'Calls', permission: 'calls:read', icon: 'phone' },
      { to: '/qualified-leads', label: 'Qualified leads', permission: 'calls:read', icon: 'badgeCheck' },
      { to: '/transfers', label: 'Transfers', permission: 'transfers:read', icon: 'transfer' },
      { to: '/leads', label: 'Leads', permission: 'leads:read', icon: 'target' },
      { to: '/compliance', label: 'DNC & callbacks', permission: 'dnc:read', icon: 'shield' },
    ],
  },
  {
    title: 'Business',
    items: [
      { to: '/analytics', label: 'Analytics', permission: 'analytics:read', icon: 'chart' },
      { to: '/usage', label: 'Usage', permission: 'usage:read', icon: 'activity' },
      // Beside Usage, because they answer adjacent questions: what
      // have I spent, and what am I allowed.
      { to: '/plan', label: 'Plan & limits', icon: 'sliders' },
      // What was bought, how much is in use, when it renews.
      { to: '/subscription', label: 'Subscription', permission: 'billing:read', icon: 'badgeCheck' },
      { to: '/billing', label: 'Billing', permission: 'billing:read', icon: 'card' },
    ],
  },
  {
    title: 'Configuration',
    items: [
      { to: '/sip', label: 'SIP & calling', permission: 'sip:read', icon: 'plug' },
      { to: '/team', label: 'Users', permission: 'client_users:read', icon: 'users' },
      { to: '/settings', label: 'Settings', permission: 'settings:read', icon: 'settings' },
    ],
  },
];

const ADMIN_NAV: NavSection[] = [
  {
    items: [
      { to: '/', label: 'Overview', end: true, icon: 'gauge' },
      { to: '/setup', label: 'Setup', icon: 'wand' },
      { to: '/live-calls', label: 'Live calls', icon: 'waves' },
      { to: '/alerting', label: 'Alerting', icon: 'bell' },
      { to: '/system', label: 'System', icon: 'cpu' },
    ],
  },
  {
    title: 'Tenants',
    items: [
      { to: '/clients', label: 'Clients', icon: 'building' },
      // Beside Clients, because that is where somebody goes looking for a
      // signup they were told about. The onboarding email says "approve the
      // request under Clients" and, until this existed, pointed at nothing.
      { to: '/access-requests', label: 'Signup requests', icon: 'userPlus' },
    ],
  },
  {
    title: 'Activity',
    items: [
      { to: '/stats', label: 'Statistics', icon: 'chart' },
      { to: '/dispositions', label: 'Dispositions', icon: 'listChecks' },
      { to: '/qualification', label: 'Qualification', icon: 'filter' },
      { to: '/leads', label: 'Leads', icon: 'target' },
    ],
  },
  {
    title: 'Product',
    items: [
      { to: '/services', label: 'Services catalog', icon: 'package' },
      { to: '/templates', label: 'Bot engines', icon: 'puzzle' },
      // Beside Bot engines, because that is where somebody goes
      // when they are about to configure one.
      { to: '/composer', label: 'Composer', icon: 'sparkles' },
      { to: '/calls', label: 'Call history', icon: 'clock' },
      { to: '/soundboards', label: 'Soundboard Bots', icon: 'music' },
      { to: '/providers', label: 'Providers & rates', icon: 'plug' },
      { to: '/provider-status', label: 'Provider status', icon: 'pulse' },
    ],
  },
  {
    title: 'Commercial',
    items: [
      // First: the money that is sold, before the money that is spent.
      { to: '/licensing', label: 'Licensing', icon: 'badgeCheck' },
      { to: '/invoices', label: 'Invoices', icon: 'fileText' },
      { to: '/revenue', label: 'Revenue & margin', icon: 'trendingUp' },
      { to: '/costs', label: 'Provider costs', icon: 'coins' },
      { to: '/plans', label: 'Plans', icon: 'sliders' },
    ],
  },
  {
    title: 'Platform',
    items: [
      { to: '/platform-users', label: 'Platform users', icon: 'users' },
      { to: '/notifications', label: 'Notifications', icon: 'mail' },
      { to: '/audit-logs', label: 'Audit logs', icon: 'fileText' },
      { to: '/diagnostics', label: 'Diagnostics', icon: 'activity' },
      // Its own entry, not a panel inside Diagnostics. It is the page
      // somebody opens while a carrier is being billed for a channel
      // nobody can find, and it is the only one with a button that
      // ends it.
      { to: '/asterisk', label: 'Asterisk channels', icon: 'server' },
      { to: '/voice-cache', label: 'Voice cache', icon: 'voice' },
      { to: '/system-logs', label: 'System logs', icon: 'terminal' },
      { to: '/branding', label: 'Branding & Theme', icon: 'palette' },
      { to: '/platform-settings', label: 'Settings', icon: 'settings' },
    ],
  },
];

function RequireAuth({ children }: { children: ReactNode }) {
  const { user, loading } = useAuth();
  if (loading) return <Loading label="Checking your session…" />;
  if (!user) return <Navigate to="/login" replace />;
  return <>{children}</>;
}

interface SetupStatus {
  needs_setup: boolean;
  version: string;
  database_ready: boolean;
  mock_mode: { vicidial: boolean; telephony: boolean; ai: boolean };
}

export default function App() {
  const { user, loading } = useAuth();
  const [justSetUp, setJustSetUp] = useState(false);

  // Ask the server whether the platform has an owner yet. Until this answers we
  // must not render a login form: on a fresh install there is nothing to log
  // into, and showing "invalid credentials" would be actively misleading.
  const setup = useQuery({
    queryKey: ['setup-status'],
    queryFn: () => api.get<SetupStatus>('/setup/status'),
    // A completed platform never goes back to needing setup.
    staleTime: Infinity,
    retry: 1,
  });

  if (setup.isPending) return <SetupLoading />;

  if (setup.data?.needs_setup && !justSetUp && !user) {
    return (
      <Setup
        status={setup.data}
        onComplete={(next: AuthUser) => {
          setJustSetUp(true);
          // Reload so AuthProvider picks up the stored session cleanly rather
          // than us hand-threading the new user through context.
          window.location.replace(next.actor === 'platform' ? '/' : '/');
        }}
      />
    );
  }

  if (loading) return <Loading label="Loading…" />;

  return (
    <Suspense fallback={<Loading />}>
      <Routes>
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/request-access" element={<RequestAccess />} />

        <Route
          path="/login"
          element={user ? <Navigate to="/" replace /> : <Login />}
        />

        <Route
          path="/"
          element={
            <RequireAuth>
              {user?.actor === 'platform' ? (
                <AppLayout
                  sections={ADMIN_NAV}
                  productLabel="Super Admin"
                  accent="platform"
                />
              ) : (
                <AppLayout
                  sections={CLIENT_NAV}
                  productLabel="Home Services"
                  accent="client"
                />
              )}
            </RequireAuth>
          }
        >
          {user?.actor === 'platform' ? (
            <>
              <Route index element={<AdminOverview />} />
              <Route path="setup" element={<AdminSetup />} />
              <Route path="live-calls" element={<AdminLiveCalls />} />
              <Route path="stats" element={<AdminStats />} />
              <Route path="dispositions" element={<AdminDispositions />} />
              <Route path="qualification" element={<AdminQualification />} />
              <Route path="leads" element={<AdminLeads />} />
              <Route path="provider-status" element={<AdminProviderStatus />} />
              <Route path="clients" element={<Clients />} />
              <Route path="access-requests" element={<AccessRequests />} />
              <Route path="clients/:clientId" element={<ClientDetail />} />
              <Route path="services" element={<ServicesCatalog />} />
              <Route path="templates" element={<Templates />} />
              <Route path="composer" element={<Composer />} />
              <Route path="soundboards" element={<Soundboards />} />
              <Route path="providers" element={<Providers />} />
              <Route path="licensing" element={<Licensing />} />
              <Route path="invoices" element={<Invoices />} />
              <Route path="revenue" element={<Revenue />} />
              <Route path="costs" element={<Costs />} />
              <Route path="plans" element={<Plans />} />
              <Route path="platform-users" element={<PlatformUsers />} />
              <Route path="audit-logs" element={<AuditLogs />} />
              <Route path="diagnostics" element={<Diagnostics />} />
          <Route path="asterisk" element={<AsteriskChannels />} />
              <Route path="voice-cache" element={<VoiceCache />} />
              <Route path="system-logs" element={<SystemLogs />} />
              <Route path="alerting" element={<Alerting />} />
              <Route path="notifications" element={<Notifications />} />
              <Route path="system" element={<SystemInfo />} />
              <Route path="calls" element={<AdminCalls />} />
              <Route path="calls/:callId" element={<AdminCallDetail />} />
              <Route path="branding" element={<AdminBranding />} />
              <Route path="platform-settings" element={<PlatformSettings />} />
            </>
          ) : (
            <>
              <Route index element={<ClientDashboard />} />
              <Route path="bots" element={<Bots />} />
              <Route path="soundboard-bots" element={<SoundboardBots />} />
              <Route path="bots/:botId" element={<BotDetail />} />
              <Route path="bot-groups" element={<BotGroups />} />
              <Route path="vicidial-servers" element={<VicidialServers />} />
              <Route path="vicidial-users" element={<VicidialUsers />} />
              <Route path="assignments" element={<Assignments />} />
              <Route path="calls" element={<Calls />} />
              <Route path="calls/live" element={<LiveCalls />} />
              <Route path="calls/:callId" element={<CallDetail />} />
              <Route path="qualified-leads" element={<QualifiedLeads />} />
              <Route path="transfers" element={<Transfers />} />
              <Route path="leads" element={<Leads />} />
              <Route path="compliance" element={<Compliance />} />
              <Route path="analytics" element={<Analytics />} />
              <Route path="usage" element={<Usage />} />
              <Route path="plan" element={<Allowances />} />
              <Route path="billing" element={<Billing />} />
              <Route path="subscription" element={<Subscription />} />
              <Route path="sip" element={<Sip />} />
              <Route path="team" element={<TeamUsers />} />
              <Route path="settings" element={<Settings />} />
            </>
          )}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Route>
      </Routes>
    </Suspense>
  );
}